import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getPageTitleSizeClass, getSectionHeadingSizeClass, getSmallTextSizeClass, getTextSizeClass } from '../../utils/fontSizeUtils';

const TermsOfUse: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { fontSize } = useFontSize();
  const isFromSignUp = location.state?.fromSignUp === true;

  useEffect(() => {
    document.title = 'Stock Table - Terms of Service';
    
    // Check if there's a hash in the URL (e.g., #refunds)
    const hash = location.hash;
    if (hash) {
      // Wait for the page to render, then scroll to the element
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          // Get the header height (64px = h-16)
          const headerHeight = 64;
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - headerHeight - 16; // Extra 16px for spacing
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const handleReturn = () => {
    if (isFromSignUp) {
      navigate('/signup');
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-background-light dark:bg-background flex flex-col">
      <Header />
      
      <main className="flex-grow max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="bg-surface-light dark:bg-background-card rounded-lg shadow-lg p-6 sm:p-8 md:p-10">
          <h1 className={`${getPageTitleSizeClass(fontSize)} font-bold text-text-light-primary dark:text-text-primary mb-2`}>
            Terms of Service
          </h1>
          <p className={`${getSmallTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary mb-8`}>
            Last Updated: February 22, 2026
          </p>

          <div className="prose prose-sm sm:prose-base max-w-none text-text-light-primary dark:text-text-primary">
            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                1. Acceptance of Terms
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                By accessing or using Stock Table ("the Service"), you agree to be bound by these Terms of Service ("Terms"). 
                If you do not agree to these Terms, please do not use the Service. We reserve the right to modify these Terms 
                at any time, and such modifications shall be effective immediately upon posting. Your continued use of the 
                Service after any such modifications constitutes your acceptance of the modified Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                2. Description of Service
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                Stock Table is a stock evaluation and comparison service that provides users with access to stock-related data and tools, 
                including but not limited to:
              </p>
              <ul className={`list-disc pl-6 mb-4 space-y-2 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                <li>Near-Real-time and historical stock price data</li>
                <li>Technical indicators and analysis tools (e.g., Bollinger Bands, RSI, MACD, Fibonacci Levels)</li>
                <li>Fundamental financial data and ratios</li>
                <li>Analyst estimates and recommendations</li>
                <li>Recent insider transactions and trends</li>
                <li>Custom ticker lists and watchlists</li>
                <li>Public list sharing functionality</li>
              </ul>

              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                Data is aggregated via APIs provided by Massive.com and SEC, and we process the data to provide indicators shown in the tables.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                3. Not Investment Advice
              </h2>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-600 p-4 mb-4">
                <p className={`font-semibold ${getTextSizeClass(fontSize)} text-yellow-800 dark:text-yellow-300 mb-2`}>
                  IMPORTANT DISCLAIMER
                </p>
                <p className={`${getTextSizeClass(fontSize)} text-yellow-700 dark:text-yellow-200 leading-relaxed`}>
                  Stock Table does not provide investment advice, financial advice, trading advice, or any other form of 
                  advice. The information, data, and tools provided by the Service are for informational and educational 
                  purposes only. Nothing on the Service should be construed as a recommendation to buy, sell, or hold any 
                  security, financial instrument, or investment.
                </p>
                <p className={`${getTextSizeClass(fontSize)} text-yellow-700 dark:text-yellow-200 leading-relaxed mt-2`}>
                  All investment decisions should be made at your own discretion and based on your own research, analysis, 
                  and judgment. You should consult with a qualified financial advisor, accountant, or other professional 
                  before making any investment decisions. Stock Table, its operators, employees, and affiliates are not 
                  responsible for any losses or damages that may result from your use of the Service or reliance on any 
                  information provided through the Service.
                </p>
              </div>
            </section>


            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                4. Data Accuracy and Availability
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                While we strive to provide accurate and up-to-date information, we do not guarantee the accuracy, 
                completeness, timeliness, or reliability of any data, information, or content available through the Service. 
                Stock market data is subject to delays, errors, and may not reflect real-time trading activity. Data may 
                be delayed by 15 minutes or more, and some data may be provided on a "best efforts" basis.
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                The Service may experience interruptions, downtime, or technical issues that prevent access to data or 
                functionality. We do not guarantee uninterrupted or error-free operation of the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                5. User Accounts
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                To access certain features of the Service, you may be required to create an account. You are responsible 
                for maintaining the confidentiality of your account credentials and for all activities that occur under 
                your account. You agree to:
              </p>
              <ul className={`list-disc pl-6 mb-4 space-y-2 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                <li>Provide accurate, current, and complete information when creating an account</li>
                <li>Maintain and update your account information to keep it accurate</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
                <li>Not share your account credentials with any third party</li>
                <li>Not create multiple accounts to circumvent usage limits or restrictions</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                6. User Content and Public Lists
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                You may create and share custom ticker lists through the Service. By creating a public list, you grant 
                Stock Table and other users the right to view and access that list. You retain ownership of your lists but 
                grant us a license to display, distribute, and use your lists as part of the Service.
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                You are solely responsible for the content of your lists and any information you share. You agree not to 
                create lists that:
              </p>
              <ul className={`list-disc pl-6 mb-4 space-y-2 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                <li>Violate any laws or regulations</li>
                <li>Infringe on the rights of others</li>
                <li>Contain false, misleading, or fraudulent information</li>
                <li>Promote illegal activities or securities fraud</li>
                <li>Contain spam, malware, or malicious content</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                7. Usage Limits and Restrictions
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                We reserve the right to impose usage limits, rate limits, or other restrictions on your use of the Service 
                to ensure fair access for all users and to protect the integrity of our systems. These limits may vary 
                based on your account type, subscription level, or other factors.
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                You agree not to:
              </p>
              <ul className={`list-disc pl-6 mb-4 space-y-2 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                <li>Use automated systems, bots, or scrapers to access the Service</li>
                <li>Attempt to reverse engineer, decompile, or disassemble any part of the Service</li>
                <li>Interfere with or disrupt the Service or servers connected to the Service</li>
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Resell, redistribute, or commercially exploit any data from the Service without authorization</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                8. Paid Plans and Pricing
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                We offer paid subscription plans that unlocks access to advanced indicators, allows you to create more lists, and enables viewing data of more tickers. All paid plans are monthly subscriptions that 
                automatically renew each month. Your subscription will begin immediately upon completion of the transaction.
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                <strong>Free trial:</strong> We offer a 14-day free trial of the Pro plan to eligible first-time subscribers. The free trial is limited to one per user (one per account, for the lifetime of that account). During the trial you have full access to Pro features and are not charged. When you start a subscription with a free trial, you provide a payment method; at the end of the 14-day trial, your paid monthly subscription begins automatically and we charge your payment method unless you have cancelled before the trial end. You can cancel the free trial at any time from the "Manage Billing" page. If you cancel, you keep Pro access until the end of the trial period and are not charged. Once you have been charged (e.g., after the trial ends or if you subscribed without a trial), the refund and cancellation terms in Sections 9 and 10 apply.
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                Payments for paid plans are processed through PayPal. You may pay using your PayPal 
                account or a credit or debit card through PayPal. By purchasing a subscription, you agree to PayPal's 
                terms and policies as they apply to your payment. Stock Table does not store your payment card details or 
                full PayPal account credentials on our servers.
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                We reserve the right to change our fees or institute new 
                fees at any time. If payment of fees is not made when due, we reserve the right to suspend or terminate your 
                access to paid features immediately without prior notice.
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                If we change our pricing, we will notify all affected users via email at least 30 days before the next billing 
                date. Any changes to fees will be effective upon posting or as otherwise communicated to you.
              </p>
            </section>

            <section id="refunds" className="mb-8" style={{ scrollMarginTop: '80px' }}>
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                9. Refunds
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                If you cancel your paid subscription and choose to end Pro immediately (via the "Manage Billing" page), we will process a refund as follows:
              </p>
              <ul className={`list-disc pl-6 mb-4 space-y-2 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                <li>
                  <strong>Full refund within 48 hours of your first charge:</strong> If you end Pro immediately within 48 hours of the date and time of your first paid charge for that subscription, you are eligible for a full refund of that charge. This 48-hour full-refund option is available only once per user across all subscriptions (if you have already received a full refund under this policy on any prior subscription, you are not eligible again). The refund is processed automatically when you confirm "End Pro immediately" on the Manage Billing page.
                </li>
                <li>
                  <strong>Prorated refund after the 48-hour window:</strong> If you end Pro immediately more than 48 hours after your first paid charge, or if you are not eligible for the full refund above, you will receive a prorated refund based on the number of days remaining in your current billing period. The refund is processed automatically when you confirm "End Pro immediately" on the Manage Billing page.
                </li>
              </ul>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                If you cancel by choosing to end Pro at the end of your billing cycle (or at the end of your free trial), no refund is issued; you retain access until that date and are not charged again.
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                Refunds are processed through PayPal. Depending on your payment method, it may take up to several business days for the refund to appear. For refund issues or requests that cannot be handled through the Service (e.g., failed automatic refund), contact us through the{' '}
                <Link
                  to="/contact-us"
                  className="font-medium text-gold-text-light dark:text-highlight-gold hover:text-opacity-80 transition-colors underline"
                >
                  contact form
                </Link>
                {' '}or at{' '}
                <a 
                  href="mailto:service@stock-table.com"
                  className="font-medium text-gold-text-light dark:text-highlight-gold hover:text-opacity-80 transition-colors underline"
                >
                  service@stock-table.com
                </a>
                .
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                <strong>Chargeback policy:</strong> If you file a chargeback with your payment provider without first contacting us to resolve the issue, you may become ineligible for future refunds under this policy. We encourage you to contact us first so we can address any concerns about your subscription or charges.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                10. Cancellation
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                You may cancel your Pro subscription at any time from the "Manage Billing" page in the Service.
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                When you cancel, you can choose one of the following:
              </p>
              <ul className={`list-disc pl-6 mb-4 space-y-2 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary`}>
                <li>
                  <strong>End Pro at the end of your billing cycle (or free trial):</strong> Your subscription will stop at the end of your current billing period or trial. You keep access to Pro features until that date. No refund is issued, and you will not be charged again after that date.
                </li>
                <li>
                  <strong>End Pro immediately:</strong> Your subscription ends right away. A refund is processed automatically as described in Section 9 (Refunds)—either a full refund if you are within the 48-hour window and eligible, or a prorated refund based on days remaining in your billing period. Your access to Pro features ends immediately.
                </li>
              </ul>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                After cancellation, your access to Pro features will end either at the chosen date (end of period or trial) or immediately, depending on the option you selected. You may resubscribe at any time from the Pricing or Manage Billing page, subject to our then-current terms and pricing.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                11. Intellectual Property
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                The Service, including its design, features, functionality, and content (excluding user-generated content), 
                is owned by Stock Table and protected by copyright, trademark, and other intellectual property laws. By using 
                the Service, you are granted a non-exclusive, non-transferable license to use the software for your personal 
                or internal business purposes in accordance with these Terms.
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                You may not copy, modify, distribute, sell, or lease any part of the Service without our prior written consent.
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                Stock market data and financial information provided through the Service may be subject to third-party 
                intellectual property rights and licensing agreements. You are responsible for complying with any 
                applicable third-party terms and restrictions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                12. Third-Party Services and Data Sources
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                The Service relies on third-party data providers for stock market data and financial information. We are not 
                responsible for the accuracy, availability, or content of data provided by third parties. Your use of 
                third-party data is subject to the terms and conditions of those third-party providers.
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                Subscription payments are processed by PayPal. Your use of PayPal for payment is subject to PayPal's 
                terms and policies. We are not responsible for PayPal's services, availability, or handling of your 
                payment data.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                13. Limitation of Liability
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, STOCK TABLE AND ITS OPERATORS, EMPLOYEES, AFFILIATES, AND DATA 
                PROVIDERS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, 
                INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR 
                USE OF OR INABILITY TO USE THE SERVICE.
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL DAMAGES EXCEED THE AMOUNT YOU PAID TO US, IF ANY, 
                FOR ACCESSING THE SERVICE IN THE TWELVE (12) MONTHS PRIOR TO THE EVENT GIVING RISE TO THE LIABILITY.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                14. Indemnification
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                You agree to indemnify, defend, and hold harmless Stock Table, its operators, employees, affiliates, and 
                data providers from any claims, damages, losses, liabilities, and expenses (including reasonable attorneys' 
                fees) arising out of or relating to your use of the Service, your violation of these Terms, or your 
                violation of any rights of another party.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                15. Termination
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                We reserve the right to suspend or terminate your access to the Service at any time, with or without cause 
                or notice, for any reason, including but not limited to violation of these Terms, fraudulent activity, or 
                abuse of the Service. Upon termination, your right to use the Service will immediately cease.
              </p>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                You may terminate your account at any time by contacting us or through account settings, if available.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                16. Privacy
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                Your use of the Service is also governed by our Privacy Policy. Please review our Privacy Policy to 
                understand how we collect, use, and protect your information.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                17. Modifications to the Service
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                We reserve the right to modify, suspend, or discontinue any part of the Service at any time, with or 
                without notice. We shall not be liable to you or any third party for any modification, suspension, or 
                discontinuance of the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                18. Governing Law and Dispute Resolution
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                These Terms shall be governed by and construed in accordance with the laws of Taiwan, without regard to its 
                conflict of law provisions. Any disputes arising out of or relating to these Terms or the Service shall be 
                resolved through binding arbitration in Taipei, Taiwan, or in the appropriate courts of Taiwan.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                19. Severability
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited 
                or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and 
                effect.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                20. Entire Agreement
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                These Terms constitute the entire agreement between you and Stock Table regarding the use of the Service 
                and supersede all prior agreements and understandings, whether written or oral.
              </p>
            </section>

            <section className="mb-8">
              <h2 className={`${getSectionHeadingSizeClass(fontSize)} font-semibold text-text-light-primary dark:text-text-primary mb-4`}>
                21. Contact Information
              </h2>
              <p className={`mb-4 ${getTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary leading-relaxed`}>
                If you have any questions about these Terms, please contact us through the{' '}
                <Link
                  to="/contact-us"
                  className="font-medium text-gold-text-light dark:text-highlight-gold hover:text-opacity-80 transition-colors underline"
                >
                  contact form
                </Link>
                {' '}provided in the Service or by emailing us at{' '}
                <a 
                  href="mailto:service@stock-table.com"
                  className="font-medium text-gold-text-light dark:text-highlight-gold hover:text-opacity-80 transition-colors underline"
                >
                  service@stock-table.com
                </a>
                .
              </p>
            </section>

            <div className="mt-10 pt-6 border-t border-border-light dark:border-border-color">
              <p className={`${getSmallTextSizeClass(fontSize)} text-text-light-secondary dark:text-text-secondary text-center`}>
                By using Stock Table, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
              <div className="mt-6 text-center">
                <button
                  onClick={handleReturn}
                  className={`inline-block px-6 py-2 bg-primary dark:bg-highlight-gold text-gray-900 dark:text-gray-900 rounded-lg font-medium hover:opacity-90 transition-opacity ${getTextSizeClass(fontSize)}`}
                >
                  {isFromSignUp ? 'Return to Sign-Up' : 'Return to Stock Table'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsOfUse;

